import React, { forwardRef } from "react";
import { fmtNumber } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const NAVY = "#0f172a";
const SLATE = "#475569";
const MUTED = "#64748b";
const BORDER = "#e2e8f0";
const CRIMSON = "#A5122F";
const W = 720;
const REMARK_PLUGINS = [remarkGfm];

const md = {
  h1: ({ node, ...p }) => <h1 style={{ fontSize: 18, color: NAVY, margin: "10px 0 6px" }} {...p} />,
  h2: ({ node, ...p }) => <h2 style={{ fontSize: 14, color: NAVY, margin: "10px 0 5px" }} {...p} />,
  p: ({ node, ...p }) => <p style={{ fontSize: 11, lineHeight: 1.5, color: SLATE, margin: "0 0 6px" }} {...p} />,
  ul: ({ node, ordered, ...p }) => <ul style={{ margin: "0 0 6px", paddingLeft: 18, fontSize: 11, color: SLATE }} {...p} />,
  ol: ({ node, ordered, ...p }) => <ol style={{ margin: "0 0 6px", paddingLeft: 18, fontSize: 11, color: SLATE }} {...p} />,
  li: ({ node, ...p }) => <li style={{ margin: "0 0 3px" }} {...p} />,
  strong: ({ node, ...p }) => <strong style={{ color: NAVY }} {...p} />,
};

const TYPES = ["calls", "emails", "tasks", "meetings", "notes"];

// Mirror of the on-screen corrText helper so the exported report reads the same.
function corrText(c) {
  if (c === null || c === undefined) return "not enough data (need 3+ reps with variance)";
  const sign = c >= 0 ? "positive" : "negative";
  const strength = Math.abs(c) >= 0.7 ? "strong" : Math.abs(c) >= 0.4 ? "moderate" : "weak";
  return `${strength} ${sign} (r = ${c})`;
}

const sectionHeading = {
  fontSize: 13,
  fontWeight: 700,
  color: NAVY,
  margin: "0 0 8px",
};

const ActivityInsightsPrint = forwardRef(function ActivityInsightsPrint({ insights, reportMd, rangeLabel }, ref) {
  const byType = (insights && insights.by_type) || [];
  const byRep = (insights && insights.by_rep) || [];
  const total = (insights && insights.total) || 0;
  const correlation = (insights && insights.correlation) || { rows: [] };
  const corrRows = correlation.rows || [];
  const countFor = (k) => byType.find((t) => t.type === k)?.count ?? 0;

  return (
    <div aria-hidden="true" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", left: 0, top: 0 }}>
      <div ref={ref} style={{ width: W, background: "#fff", color: NAVY, fontFamily: "Arial, Helvetica, sans-serif", padding: "0 32px 32px" }}>
        <div style={{ borderBottom: `3px solid ${CRIMSON}`, paddingBottom: 12, marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>SalesTap — Activity Insights</h1>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: MUTED }}>
            {fmtNumber(total)} activities{rangeLabel ? `  ·  ${rangeLabel}` : ""}
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {TYPES.map((t) => (
            <div key={t} style={{ flex: 1, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 6px", textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: NAVY }}>{fmtNumber(countFor(t))}</div>
              <div style={{ fontSize: 9, color: MUTED, textTransform: "capitalize" }}>{t}</div>
            </div>
          ))}
        </div>

        {byRep.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={sectionHeading}>Activity by Rep</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${NAVY}` }}>
                  <th style={{ textAlign: "left", padding: 4 }}>Rep</th>
                  {TYPES.map((t) => (
                    <th key={t} style={{ textAlign: "right", padding: 4, textTransform: "capitalize" }}>{t}</th>
                  ))}
                  <th style={{ textAlign: "right", padding: 4, fontWeight: 700 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {byRep.map((r) => (
                  <tr key={r.owner_id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <td style={{ padding: 4 }}>{r.name}</td>
                    {TYPES.map((t) => (
                      <td key={t} style={{ textAlign: "right", padding: 4, color: SLATE }}>{fmtNumber(r[t] ?? 0)}</td>
                    ))}
                    <td style={{ textAlign: "right", padding: 4, fontWeight: 700 }}>{fmtNumber(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(corrRows.length > 0 ||
          correlation.activities_vs_wins != null ||
          correlation.meetings_vs_wins != null) && (
          <div style={{ marginBottom: 16 }}>
            <div style={sectionHeading}>Activity &#8596; Outcomes</div>
            <div style={{ fontSize: 10.5, color: MUTED, margin: "0 0 8px" }}>
              <div>
                Activities vs. wins:{" "}
                <span style={{ color: NAVY, fontWeight: 600 }}>{corrText(correlation.activities_vs_wins)}</span>
              </div>
              <div>
                Meetings vs. wins:{" "}
                <span style={{ color: NAVY, fontWeight: 600 }}>{corrText(correlation.meetings_vs_wins)}</span>
              </div>
            </div>
            {corrRows.length > 0 && (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${NAVY}` }}>
                    <th style={{ textAlign: "left", padding: 4 }}>Rep</th>
                    <th style={{ textAlign: "right", padding: 4 }}>Activities</th>
                    <th style={{ textAlign: "right", padding: 4 }}>Meetings</th>
                    <th style={{ textAlign: "right", padding: 4 }}>Won</th>
                  </tr>
                </thead>
                <tbody>
                  {corrRows.map((r) => (
                    <tr key={r.owner_id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                      <td style={{ padding: 4 }}>{r.name}</td>
                      <td style={{ textAlign: "right", padding: 4 }}>{fmtNumber(r.activities)}</td>
                      <td style={{ textAlign: "right", padding: 4, color: SLATE }}>{fmtNumber(r.meetings)}</td>
                      <td style={{ textAlign: "right", padding: 4 }}>{fmtNumber(r.won_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div style={{ borderTop: `2px solid ${CRIMSON}`, paddingTop: 12 }}>
          {reportMd ? (
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={md}>{reportMd}</ReactMarkdown>
          ) : (
            <p style={{ fontSize: 11.5, color: MUTED }}>No activity analysis generated yet.</p>
          )}
        </div>
      </div>
    </div>
  );
});

export default ActivityInsightsPrint;
